import {
  type CreateReagentItemInput,
  type Department,
  type ReagentItemStock,
  type Supplier,
  createReagentItemSchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { ReagentIdentityCard, ReagentPackCard } from './ReagentItemFields';
import { hasStockMoved, initialReagentItemValues } from './reagent-item-form.logic';
import { useAllRecords } from '#client/hooks/useAllRecords';

const PATH = RESOURCE.reagentItems;

export function ReagentItemFormPage() {
  return (
    <ResourceFormShell<ReagentItemStock> path={PATH} noun="reagent">
      {({ id, record }) => <ReagentItemForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function ReagentItemForm({
  id,
  record,
}: {
  id?: string | undefined;
  record?: ReagentItemStock | undefined;
}) {
  const departments = useAllRecords<Department>({ path: RESOURCE.departments });
  const suppliers = useAllRecords<Supplier>({ path: RESOURCE.suppliers });

  const { values, set } = useFormValues<CreateReagentItemInput>(initialReagentItemValues(record));

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createReagentItemSchema,
    noun: 'Reagent',
  });

  return (
    <form onSubmit={form.handleSubmit(values)} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <ReagentIdentityCard
          values={values}
          onFieldChange={set}
          errors={form.errors}
          departments={departments.items}
          suppliers={suppliers.items}
        />

        <ReagentPackCard
          values={values}
          onFieldChange={set}
          errors={form.errors}
          hasMoved={hasStockMoved(record)}
        />

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Add reagent'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
